import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { BlobWriter, ZipWriter } from "@zip.js/zip.js";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
} from "@ey-xd/motif-react";
import "./OrdersDetails.scss";
import { UploadSquare } from "../../assets/Icons/UploadSquare";
import { MoreHorizantal } from "../../assets/Icons/MoreHorizantal";
import { PlusIcon } from "../../assets/Icons/PlusIcon";
import { FilterIcon } from "../../icons/filterIcon";
import { SortIcon } from "../../assets/Icons/SortIcon";
import { MagnifyingGlass } from "../../assets/Icons/MagnifyingGlass";
import { UploadNewFile } from "./UploadNewFile";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import { MoreVertical } from "../../assets/Icons/MoreVertical";
import { EditIcon } from "../../assets/Icons/EditIcon";
import { DeleteIcon } from "../../assets/Icons/DeleteIcon";
import { ReviewMappingsModal } from "./ReviewMappingsModal";
import { ReplaceFileModal } from "./ReplaceFileModal";
import { FilesService } from "../../api/services/files.service";
import { RefreshIcon } from "../../assets/Icons/Refresh";
import WarningTriangle from "../../assets/Icons/WarningTriangle";
import { FILE_UPLOAD } from "../../util/constants";

// Chunk size for Azure Blob uploads (4MB - recommended block size)
const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB

// Helper function to get fresh SAS URL from the API
const getAzureBlobSasUrl = async () => {
  const { sasUrl } = await FilesService.getSasToken();
  return sasUrl;
};

// Component for managing file uploads with accordion-based organization

export const OrderDetailsFileUpload = ({
  onDataReceivedFlowChange,
  onIncompleteData,
}) => {
  // Get currentOrder from Redux to extract the actual orderId (requestId)
  const currentOrder = useSelector((state) => state.orders?.currentOrder);
  const user = useSelector((state) => state.user?.user);
  const actualOrderId = currentOrder?.[0]?.headerSection?.[0]?.requestId;

  // Get axonId for blob folder structure
  const axonId = currentOrder?.[0]?.headerSection?.[0]?.axonId || actualOrderId;

  // Check if order status is "Data Received" (case-insensitive)
  // Attach Files and Field Mapping buttons are only enabled when status is "Data Received"
  const isDataReceivedStatus = useMemo(() => {
    const headerData = currentOrder?.[0]?.headerSection?.[0] || {};
    const currentStatus = headerData.currentSubStage || headerData.status || "";
    return currentStatus.toLowerCase() === "data received";
  }, [currentOrder]);

  // Helper function to get countryId from order details pagefields
  const getCountryIdFromOrderDetails = () => {
    if (!Array.isArray(currentOrder)) return null;

    // Find the countryId field (case-insensitive)
    const countryField = currentOrder?.[0]?.pagefields?.find((field) => {
      const fieldName = String(field?.field || "").toLowerCase();
      return fieldName === "countryid" || fieldName === "country";
    });

    // Return fieldValueId as a number, fallback to null
    if (
      countryField?.fieldValueId !== undefined &&
      countryField?.fieldValueId !== null
    ) {
      return parseInt(countryField.fieldValueId, 10) || null;
    }

    return null;
  };

  const [activeTab, setActiveTab] = useState("CY");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [showReviewMappings, setShowReviewMappings] = useState(false);
  const [selectedFileForMapping, setSelectedFileForMapping] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [fileToReplace, setFileToReplace] = useState(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [replaceUploadProgress, setReplaceUploadProgress] = useState(0); // 0-100 percentage
  const [isDownloadingOutput, setIsDownloadingOutput] = useState(false);

  // State for files fetched from API
  const [fileSections, setFileSections] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState(null);
  const actionMenuRef = useRef(null);

  // Filter file sections based on selected CY/PY tab and search query
  const filteredFileSections = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    return fileSections
      .map((section) => ({
        ...section,
        files: section.files.filter((file) => {
          const filePeriod = (file.period || "CY").toUpperCase();
          const matchesPeriod = filePeriod === activeTab;

          // If no search query, just filter by period
          if (!normalizedSearchQuery) {
            return matchesPeriod;
          }

          // Search in file name, type, and status
          const fileName = (file.name || "").toLowerCase();
          const fileType = (file.type || "").toLowerCase();
          const fileStatus = (file.status || "").toLowerCase();

          const matchesSearch =
            fileName.includes(normalizedSearchQuery) ||
            fileType.includes(normalizedSearchQuery) ||
            fileStatus.includes(normalizedSearchQuery);

          return matchesPeriod && matchesSearch;
        }),
      }))
      .filter((section) => section.files.length > 0); // Only show sections that have files for the selected period
  }, [fileSections, activeTab, searchQuery]);

  // Check if Field Mapping button should be disabled
  // Disabled when: no files exist OR any file has status: attached, mapping underway, extracting headers (case-insensitive)
  const isFieldMappingDisabled = useMemo(() => {
    // Disable if no files exist
    console.log(fileSections, "File sections");
    const totalFiles = fileSections.reduce(
      (count, section) => count + section.files.length,
      0,
    );
    if (totalFiles === 0 || fileSections.length === 0) return true;

    const disabledStatuses = [
      "attached",
      "mapping underway",
      "extracting headers",
    ];

    return fileSections.some((section) =>
      section.files.some((file) => {
        const fileStatus = (file.status || "").toLowerCase();
        return disabledStatuses.some((status) => fileStatus.includes(status));
      }),
    );
  }, [fileSections]);

  // Check if header extraction is in progress (any file has status: attached, mapping underway, extracting headers)
  const isHeaderExtractionInProgress = useMemo(() => {
    const extractionStatuses = [
      "attached",
      "mapping underway",
      "extracting headers",
    ];

    return fileSections.some((section) =>
      section.files.some((file) => {
        const fileStatus = (file.status || "").toLowerCase();
        return extractionStatuses.some((status) => fileStatus.includes(status));
      }),
    );
  }, [fileSections]);

  // Check if any file has Data Triage Success, Data Triage Failure, or Failed status (show Download Output button)
  const showDownloadOutputButton = useMemo(() => {
    const downloadStatuses = [
      "data triage performed",
      "data triage failure",
      "failed",
    ];

    return fileSections.some((section) =>
      section.files.some((file) => {
        const fileStatus = (file.status || "").toLowerCase();
        return downloadStatuses.some((status) => fileStatus.includes(status));
      }),
    );
  }, [fileSections]);

  const isDownloadOutputDisabled = useMemo(
    () =>
      fileSections.some((section) =>
        section.files.some((file) => {
          const downloadLink = file.downloadLink
          return String(downloadLink ?? "").trim() === "";
        }),
      ),
    [fileSections],
  );

  // Fetch files from API when component mounts or orderId changes
  useEffect(() => {
    const fetchFiles = async () => {
      if (!actualOrderId) {
        console.log("No orderId available to fetch files");
        return;
      }

      setIsLoadingFiles(true);
      setFilesError(null);

      try {
        const response = await FilesService.getALLFiles(actualOrderId);

        const filesByType = {};
        // Safely extract files array from various response structures
        const rawFiles =
          response?.data?.files ||
          response?.files ||
          response?.data ||
          response;
        const files = Array.isArray(rawFiles) ? rawFiles : [];

        // Filter out files with status "Replaced"
        const filteredFiles = files.filter((file) => {
          const status = file.status || "Uploaded";
          return status.toLowerCase() !== "replaced";
        });

        filteredFiles.forEach((file) => {
          const fileType = file.fileType || "Other";
          if (!filesByType[fileType]) {
            filesByType[fileType] = [];
          }
          filesByType[fileType].push({
            id: file.id || file.fileId || Date.now() + Math.random(),
            name: file.fileName || file.name || "Unknown",
            type: file.fileType || file.extension || "File",
            status: file.status || "Uploaded",
            modified:
              file.uploadedWhen ||
              file.modifiedDate ||
              file.uploadedDate ||
              new Date().toLocaleDateString(),
            period: file.period || "CY",
            ...file,
          });
        });

        // Convert to sections array
        const sections = Object.entries(filesByType).map(([type, files]) => ({
          id: type.toLowerCase().replace(/\s+/g, ""),
          title: getFileTypeName(type),
          files: files,
        }));

        setFileSections(sections);
      } catch (error) {
        console.error("Failed to fetch files:", error);
        setFilesError(error.message || "Failed to fetch files");
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchFiles();
  }, [actualOrderId]);

  // Function to refresh files list (can be called after upload)
  const refreshFilesList = async () => {
    if (!actualOrderId) return;

    setIsLoadingFiles(true);
    setFilesError(null);

    try {
      const response = await FilesService.getALLFiles(actualOrderId);
      const filesByType = {};
      // Safely extract files array from various response structures
      const rawFiles =
        response?.data?.files || response?.files || response?.data || response;
      const files = Array.isArray(rawFiles) ? rawFiles : [];

      // Filter out files with status "Replaced"
      const filteredFiles = files.filter((file) => {
        const status = file.status || "Uploaded";
        return status.toLowerCase() !== "replaced";
      });

      filteredFiles.forEach((file) => {
        const fileType = file.fileType || "Other";
        if (!filesByType[fileType]) {
          filesByType[fileType] = [];
        }
        filesByType[fileType].push({
          id: file.id || file.fileId || Date.now() + Math.random(),
          name: file.fileName || file.name || "Unknown",
          type: file.fileType || file.extension || "File",
          status: file.status || "Uploaded",
          modified:
            file.uploadedWhen ||
            file.modifiedDate ||
            file.uploadedDate ||
            new Date().toLocaleDateString(),
          period: file.period || "CY",
          ...file,
        });
      });

      const sections = Object.entries(filesByType).map(([type, files]) => ({
        id: type.toLowerCase().replace(/\s+/g, ""),
        title: getFileTypeName(type),
        files: files,
      }));

      setFileSections(sections);
    } catch (error) {
      console.error("Failed to refresh files:", error);
      setFilesError(error.message || "Failed to refresh files");
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // Helper to get display name for file type
  const getFileTypeName = (type) => {
    const typeNames = {
      JE: "Journal Entry",
      TB: "Trial Balance",
      COA: "Chart of Accounts",
    };
    return typeNames[type] || type;
  };

  // Helper to format date for display (e.g., "20 Dec, 2025" and "05:30 PM")
  const formatModifiedDate = (dateString) => {
    if (!dateString) return { date: "", time: "" };

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If invalid date, try to parse as-is
        return { date: dateString, time: "" };
      }

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const day = date.getDate().toString().padStart(2, "0");
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const formattedHours = hours.toString().padStart(2, "0");

      return {
        date: `${day} ${month}, ${year}`,
        time: `${formattedHours}:${minutes} ${ampm}`,
      };
    } catch {
      return { date: dateString, time: "" };
    }
  };

  // Closes action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setActiveActionMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Opens the disclaimer modal first before showing upload modal
  const handleOpenUploadModal = () => {
    setShowDisclaimerModal(true);
  };

  // Handles disclaimer confirmation - closes disclaimer and opens upload modal
  const handleDisclaimerConfirm = () => {
    setShowDisclaimerModal(false);
    setShowUploadModal(true);
  };

  // Closes the disclaimer modal
  const handleCloseDisclaimerModal = () => {
    setShowDisclaimerModal(false);
  };

  // Closes the upload file modal and refreshes the files list
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    // Refresh files list after modal closes to show newly uploaded files
    refreshFilesList();
  };

  // Toggles accordion section expansion
  const toggleAccordion = (accordionId) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionId]: !prev[accordionId],
    }));
  };

  // Returns CSS class based on file status
  const getStatusClass = (status) => {
    const statusLower = (status || "").toLowerCase();

    // DataTriage process Completed - green
    if (
      statusLower === "datatriage process completed" ||
      statusLower === "datatriageprocesscompleted" ||
      statusLower === "data triage performed"
    ) {
      return "status-completed";
    }

    // Extracting headers, headers mapped, mapping underway - orange/warning
    if (
      statusLower === "headers mapped" ||
      statusLower === "mapping underway" ||
      statusLower.includes("mapping")
    ) {
      return "status-processing";
    }

    if (
      statusLower === "mapped" ||
      statusLower === "data triage process initiated"
    ) {
      return "status-initiated";
    }

    // Attached - keep as default attached style
    if (statusLower === "extracting headers" || statusLower === "attached") {
      return "status-attached";
    }

    // Any failed status - red/error
    if (
      statusLower.includes("failed") ||
      statusLower.includes("error") ||
      statusLower.includes("missing")
    ) {
      return "status-failed";
    }

    // Default fallback
    return "status-default";
  };

  // Toggles action menu for a file row
  const toggleActionMenu = (fileId, event) => {
    event.stopPropagation();
    setActiveActionMenu((prev) => (prev === fileId ? null : fileId));
  };

  // Opens field mapping modal for selected file
  const handleEdit = (file) => {
    setSelectedFileForMapping(file);
    setShowReviewMappings(true);
    setActiveActionMenu(null);
  };

  // Closes review mappings modal
  const handleReviewMappingsClose = () => {
    setShowReviewMappings(false);
    setSelectedFileForMapping(null);
  };

  // Confirms mapping and refreshes the files list
  const handleReviewMappingsConfirm = async () => {
    setShowReviewMappings(false);
    setSelectedFileForMapping(null);
    // Refresh files list to reflect updated mapping status
    await refreshFilesList();
  };

  // Deletes selected file by calling delete API and refreshing the list
  const handleDelete = async (file) => {
    setActiveActionMenu(null);

    // Get the fileUploadId from the file object
    const fileUploadId = file.fileUploadId || file.id;

    if (!fileUploadId) {
      console.error("No fileUploadId found for file:", file);
      return;
    }

    // Get deletedBy from user info
    const deletedBy = user?.mail || user?.username || user?.name || "";

    try {
      // Call delete API with fileUploadId and deletedBy as query param
      await FilesService.deleteOneFile(fileUploadId, deletedBy);

      // Refresh the files list after successful deletion
      await refreshFilesList();
    } catch (error) {
      console.error("Failed to delete file:", error);
      // Optionally show error to user
    }
  };

  // Gets all existing file names for duplicate validation
  const getAllExistingFileNames = () => {
    const allFiles = [];
    fileSections.forEach((section) => {
      section.files.forEach((file) => {
        allFiles.push({
          fileName: file.name,
          name: file.name,
          id: file.id,
          fileUploadId: file.fileUploadId,
        });
      });
    });
    return allFiles;
  };

  // Validates replacement file for duplicates
  const validateReplacementFile = async (
    file,
    fileType,
    existingFiles = [],
  ) => {
    const errors = [];

    // Get all existing files in the order
    const allExistingFiles = getAllExistingFileNames();

    // Check for duplicate file name (excluding the file being replaced)
    const isDuplicate = allExistingFiles.some((existingFile) => {
      // Skip the file that is being replaced
      if (
        existingFile.id === fileToReplace?.id ||
        existingFile.fileUploadId === fileToReplace?.fileUploadId
      ) {
        return false;
      }
      return existingFile.name?.toLowerCase() === file.name.toLowerCase();
    });

    if (isDuplicate) {
      errors.push(
        "File already exists in the order. Please rename the file or choose a different file.",
      );
    }

    // Validate file extension
    const allowedExtensions = ["xlsx", "xlsm", "xls", "txt", "csv"];
    const extension = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      errors.push(
        "Invalid file type. Allowed types: xlsx, xlsm, xls, txt, csv",
      );
    }

    // Validate file size (max 5GB)
    const maxFileSize = 5000 * 1024 * 1024;
    if (file.size > maxFileSize) {
      errors.push("File size exceeds maximum limit of 5GB");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Opens replace modal for a specific file
  const handleReplace = (file) => {
    setActiveActionMenu(null);
    setFileToReplace(file);
    setShowReplaceModal(true);
  };

  // Closes replace modal
  const handleReplaceModalClose = () => {
    setShowReplaceModal(false);
    setFileToReplace(null);
  };

  // Generates a unique block ID for Azure Block Blob
  const generateBlockId = (index) => {
    const blockIdString = String(index).padStart(6, "0");
    return btoa(blockIdString);
  };

  // Uploads a single block to Azure Blob Storage
  const uploadBlock = async (blobUrl, blockId, chunk) => {
    const blockUrl = `${blobUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

    const response = await fetch(blockUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/octet-stream",
        "Content-Length": chunk.size.toString(),
        "x-ms-version": "2020-10-02",
      },
      body: chunk,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Block upload failed: ${response.status} - ${errorText}`);
    }

    return true;
  };

  // Commits all uploaded blocks to finalize the blob
  const commitBlocks = async (blobUrl, blockIds, contentType) => {
    const commitUrl = `${blobUrl}&comp=blocklist`;

    const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
<BlockList>
${blockIds.map((id) => `  <Latest>${id}</Latest>`).join("\n")}
</BlockList>`;

    const response = await fetch(commitUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/xml",
        "x-ms-blob-content-type": contentType || "application/octet-stream",
      },
      body: blockListXml,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Commit blocks failed: ${response.status} - ${errorText}`,
      );
    }

    return true;
  };

  // Uploads file to Azure Blob Storage using chunked upload
  const uploadFileToBlob = async (file, period, fileType, onProgress) => {
    if (!file) {
      throw new Error("File object not found");
    }

    if (!axonId) {
      throw new Error("Axon ID not found");
    }

    const fileTypeFolder = fileType || "Other";
    const blobName = file.name;
    // Folder structure: axonId/period/fileType/filename
    const blobPath = `${axonId}/${period}/${fileTypeFolder}/${blobName}`;

    // Get fresh SAS token and construct the full URL for the blob
    const sasUrl = await getAzureBlobSasUrl();
    const [containerUrl, sasToken] = sasUrl.includes("?")
      ? [sasUrl.split("?")[0], "?" + sasUrl.split("?")[1]]
      : [sasUrl, ""];
    const blobUrl = `${containerUrl}/DataTriage/${blobPath}${sasToken}`;

    // Calculate total chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadedBlocks = [];

    try {
      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const blockId = generateBlockId(i);

        await uploadBlock(blobUrl, blockId, chunk);
        uploadedBlocks.push(blockId);

        // Calculate and report progress
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        if (onProgress) {
          onProgress(progress);
        }

        console.log(
          `Uploaded block ${i + 1}/${totalChunks} for ${file.name} (${progress}%)`,
        );
      }

      // Commit all blocks to finalize the blob
      await commitBlocks(blobUrl, uploadedBlocks, file.type);

      console.log(`File uploaded to blob: ${blobPath}`);

      return {
        blobName,
        blobPath,
        blobUrl: `${containerUrl}/DataTriage/${blobPath}`,
        success: true,
      };
    } catch (error) {
      console.error("Blob upload failed:", error);
      throw error;
    }
  };

  // Confirms replacement - uploads to blob first, then calls replace API
  const handleConfirmReplace = async (newFile) => {
    if (!newFile || !fileToReplace) {
      return;
    }

    setIsReplacing(true);
    setReplaceUploadProgress(0);

    try {
      // Get file metadata
      const period = fileToReplace.period || "CY";
      const fileType = fileToReplace.type || fileToReplace.fileType || "Other";

      // Step 1: Upload the file to Azure Blob Storage
      console.log("Uploading replacement file to blob storage...");
      const blobResult = await uploadFileToBlob(
        newFile.file,
        period,
        fileType,
        (progress) => {
          setReplaceUploadProgress(progress);
        },
      );
      console.log("Blob upload successful:", blobResult);

      // Step 2: Build the payload for the replace API with blob path
      const payload = {
        existingFileUploadId: fileToReplace.fileUploadId || fileToReplace.id,
        orderId: actualOrderId?.toString() || "",
        countryId: getCountryIdFromOrderDetails() || 3,
        period: period,
        fileType: fileType,
        fileName: newFile.name,
        uploadedBy: user?.mail || user?.username || user?.name || "",
        filePath: blobResult.blobPath,
        blobUrl: blobResult.blobUrl,
      };

      console.log("Replacing file with payload:", payload);

      // Step 3: Call the replace API
      await FilesService.replaceFile(payload);

      // Close the modal
      setShowReplaceModal(false);
      setFileToReplace(null);

      // Refresh the files list after successful replacement
      await refreshFilesList();
    } catch (error) {
      console.error("Failed to replace file:", error);
      // Optionally show error to user
    } finally {
      setIsReplacing(false);
      setReplaceUploadProgress(0);
    }
  };

  const getHeaderStatus = (status) => {
    const statusLower = (status || "").toLowerCase();
    const headerStatuses = [
      "attached",
      "mapping underway",
      "extracting headers",
    ];
    return headerStatuses.some((s) => statusLower.includes(s));
  };

  // List blobs in a specific folder from Azure Blob Storage (handles pagination and nested folders)
  const listBlobsInFolder = async (folderPath, sasUrl) => {
    const [containerUrl, sasToken] = sasUrl.includes("?")
      ? [sasUrl.split("?")[0], "?" + sasUrl.split("?")[1]]
      : [sasUrl, ""];

    const allBlobs = [];
    let marker = "";

    // Loop to handle pagination (Azure returns max 5000 blobs per request)
    do {
      // Azure List Blobs API - no delimiter to get all nested files
      let listUrl = `${containerUrl}${sasToken}&restype=container&comp=list&prefix=${encodeURIComponent(folderPath)}`;

      // Add marker for pagination if we have one
      if (marker) {
        listUrl += `&marker=${encodeURIComponent(marker)}`;
      }

      console.log("Listing blobs with URL:", listUrl);

      const response = await fetch(listUrl, {
        method: "GET",
        headers: {
          "x-ms-version": "2020-10-02",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to list blobs: ${response.status} - ${errorText}`,
        );
      }

      const xmlText = await response.text();
      console.log("Blob listing XML response:", xmlText);

      // Parse XML response to extract blob names
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "text/xml");
      const blobElements = xmlDoc.getElementsByTagName("Blob");

      for (let i = 0; i < blobElements.length; i++) {
        const nameElement = blobElements[i].getElementsByTagName("Name")[0];
        const propertiesElement =
          blobElements[i].getElementsByTagName("Properties")[0];

        if (nameElement) {
          const blobName = nameElement.textContent;

          // Get content length to filter out zero-byte folder placeholders
          let contentLength = 0;
          if (propertiesElement) {
            const contentLengthElement =
              propertiesElement.getElementsByTagName("Content-Length")[0];
            if (contentLengthElement) {
              contentLength = parseInt(contentLengthElement.textContent, 10);
            }
          }

          // Only include actual files:
          // - Not ending with / (folder markers)
          // - Has content (content-length > 0)
          // - Has a file extension (contains a dot in the filename)
          const fileName = blobName.split("/").pop();
          const hasExtension = fileName.includes(".");

          if (!blobName.endsWith("/") && contentLength > 0 && hasExtension) {
            allBlobs.push(blobName);
            console.log("Found blob:", blobName, "Size:", contentLength);
          } else {
            console.log(
              "Skipping folder/empty blob:",
              blobName,
              "Size:",
              contentLength,
            );
          }
        }
      }

      // Check for NextMarker for pagination
      const nextMarkerElement = xmlDoc.getElementsByTagName("NextMarker")[0];
      marker = nextMarkerElement ? nextMarkerElement.textContent : "";
    } while (marker);

    console.log(`Total blobs found: ${allBlobs.length}`);
    return allBlobs;
  };

  // Download a single file from Azure Blob Storage and return as blob
  const downloadBlobFileAsBlob = async (blobPath, sasUrl) => {
    const [containerUrl, sasToken] = sasUrl.includes("?")
      ? [sasUrl.split("?")[0], "?" + sasUrl.split("?")[1]]
      : [sasUrl, ""];

    const blobUrl = `${containerUrl}/${blobPath}${sasToken}`;

    const response = await fetch(blobUrl, {
      method: "GET",
      headers: {
        "x-ms-version": "2020-10-02",
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`File not found: ${blobPath}`);
        return null;
      }
      throw new Error(`Failed to download file: ${response.status}`);
    }

    return await response.blob();
  };

  // Handle Download Output button click - downloads all files from Output folder as ZIP
  const handleDownloadOutput = async () => {
    if (!actualOrderId) {
      console.error("No order ID available for download");
      return;
    }

    if (isDownloadOutputDisabled) {
      return;
    }

    setIsDownloadingOutput(true);

    try {
      // Get fresh SAS token before download
      const sasUrl = await getAzureBlobSasUrl();

      // Construct the folder path: DataTriage_Processing/{orderId}/{orderId}_Output/
      const outputFolderPath = `DataTriage_Processing/${actualOrderId}/${actualOrderId}_Output/`;

      console.log("Listing blobs in folder:", outputFolderPath);

      // List all blobs in the Output folder
      const blobs = await listBlobsInFolder(outputFolderPath, sasUrl);

      if (blobs.length === 0) {
        console.log("No files found in Output folder");
        alert("No output files found to download.");
        return;
      }

      console.log(`Found ${blobs.length} files to download:`, blobs);

      // Create a new ZIP file using zip.js
      const zipBlobWriter = new BlobWriter("application/zip");
      const zipWriter = new ZipWriter(zipBlobWriter);

      let downloadedCount = 0;

      // Download each file and add to ZIP
      for (let i = 0; i < blobs.length; i++) {
        const blobPath = blobs[i];

        // Get relative path within Output folder for ZIP structure
        // e.g., "DataTriage_Processing/123/Output/processed/CY/JE/file.xlsx" -> "processed/CY/JE/file.xlsx"
        const relativePath = blobPath.replace(outputFolderPath, "");

        console.log(
          `Downloading file ${i + 1}/${blobs.length}: ${relativePath}`,
        );

        try {
          const fileBlob = await downloadBlobFileAsBlob(blobPath, sasUrl);

          if (fileBlob) {
            // Add file to ZIP with its relative path (preserves folder structure)
            await zipWriter.add(relativePath, fileBlob.stream());
            downloadedCount++;
          }
        } catch (downloadError) {
          console.error(
            `Failed to download file ${relativePath}:`,
            downloadError,
          );
        }
      }

      if (downloadedCount === 0) {
        await zipWriter.close();
        alert("No output files found to download.");
        return;
      }

      // Close the ZIP writer and get the blob
      console.log(`Creating ZIP file with ${downloadedCount} files...`);
      const zipBlob = await zipWriter.close();

      // Trigger download of the ZIP file
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Output_${actualOrderId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log(`Successfully downloaded ${downloadedCount} files as ZIP`);
    } catch (error) {
      console.error("Failed to download output files:", error);
      alert("Failed to download output files. Please try again.");
    } finally {
      setIsDownloadingOutput(false);
    }
  };

  return (
    <div className="order-details-file-upload-main">
      <div className="order-details-file-upload-header">
        <div className="order-details-file-upload-header-left-section">
          <button
            className={`file-upload-tab ${activeTab === "CY" ? "active" : ""}`}
            onClick={() => setActiveTab("CY")}
          >
            Current Year (CY)
          </button>
          <button
            className={`file-upload-tab ${activeTab === "PY" ? "active" : ""}`}
            onClick={() => setActiveTab("PY")}
          >
            Previous Year (PY)
          </button>
        </div>
        <div className="order-details-file-upload-header-right-section">
          <button
            className="file-upload-refresh-button"
            onClick={refreshFilesList}
            disabled={isLoadingFiles}
            title="Refresh files list"
          >
            <RefreshIcon
              width={16}
              height={16}
              color={isLoadingFiles ? "#A4A3B1" : "#dedee2"}
            />
          </button>
          {showDownloadOutputButton && (
            <button
              className="file-upload-text-button"
              onClick={handleDownloadOutput}
              disabled={isDownloadingOutput || isDownloadOutputDisabled}
              title="Download Output"
            >
              {isDownloadingOutput ? "Downloading..." : "Triage Output"}
            </button>
          )}
          <button
            className={`file-upload-text-button ${!isDataReceivedStatus ? 'disabled' : ''}`}
            onClick={() => handleEdit("true")}
            disabled={isLoadingFiles || isFieldMappingDisabled || !isDataReceivedStatus}
            title={!isDataReceivedStatus ? "Field Mapping is available only when order status is Data Received" : "Field Mapping"}
          >
            Field Mapping
          </button>
          <button
            className={`file-upload-attach-button ${!isDataReceivedStatus ? 'disabled' : ''}`}
            onClick={handleOpenUploadModal}
            disabled={!isDataReceivedStatus}
            title={!isDataReceivedStatus ? "Attach Files is available only when order status is Data Received" : "Attach Files"}
          >
            <PlusIcon width={16} height={16} color={!isDataReceivedStatus ? "#737387" : "#1A1A24"} />
            <span>Attach Files</span>
          </button>
        </div>
      </div>
      <div className="order-details-file-upload-body">
        {isHeaderExtractionInProgress && (
          <div className="header-extraction-progress-message">
            <span>
              Header Extraction is in progress. Please refresh for updates...
            </span>
          </div>
        )}
        <div className="file-upload-search-container">
          <SearchIcon width={17} height={17} color="#737387" />
          <input
            type="text"
            className="file-upload-search-input"
            placeholder="Search ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="file-upload-table-scroll">
          <table className="file-upload-table">
            <thead>
              <tr>
                <th className="file-name-header">
                  <div className="header-content">
                    <span>File Name</span>
                  </div>
                </th>
                <th className="file-type-header">
                  <div className="header-content">
                    <span>Type</span>
                    <FilterIcon />
                  </div>
                </th>
                <th className="status-header">
                  <div className="header-content">
                    <span>Status</span>
                    <FilterIcon />
                  </div>
                </th>
                <th className="modified-header">
                  <div className="header-content">
                    <span>Modified</span>
                    <SortIcon width={16} height={16} color="#A4A3B1" />
                  </div>
                </th>
                <th className="actions-header">
                  <div className="header-content">
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Loading state */}
              {isLoadingFiles && (
                <tr>
                  <td colSpan={5} className="loading-row">
                    <div className="loading-message">Loading files...</div>
                  </td>
                </tr>
              )}

              {/* Error state */}
              {filesError && !isLoadingFiles && (
                <tr>
                  <td colSpan={5} className="error-row">
                    <div className="error-message">{filesError}</div>
                  </td>
                </tr>
              )}

              {/* Empty state */}
              {!isLoadingFiles &&
                !filesError &&
                filteredFileSections.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      <div className="empty-message">
                        {fileSections.length === 0
                          ? 'No files uploaded yet. Click "Attach Files" to upload.'
                          : `No files found for ${activeTab === "CY" ? "Current Year (CY)" : "Previous Year (PY)"}.`}
                      </div>
                    </td>
                  </tr>
                )}

              {/* Accordion sections from API */}
              {!isLoadingFiles &&
                filteredFileSections.map((section) => (
                  <React.Fragment key={section.id}>
                    {/* Accordion Header Row */}
                    <tr
                      className={`accordion-header-row ${expandedAccordions[section.id] ? "expanded" : ""}`}
                      onClick={() => toggleAccordion(section.id)}
                    >
                      <td colSpan={5}>
                        <div className="accordion-header-content">
                          <div className="accordion-header-left">
                            <span className="accordion-title">
                              {section.title}
                            </span>
                            <span className="accordion-count">
                              ({section.files.length})
                            </span>
                          </div>
                          <span
                            className={`accordion-arrow ${expandedAccordions[section.id] ? "expanded" : ""}`}
                          >
                            <NavDownArrow
                              width={16}
                              height={16}
                              color="#A4A3B1"
                            />
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Accordion Content Rows - File entries */}
                    {expandedAccordions[section.id] &&
                      section.files.map((file) => (
                        <tr key={file.id} className="file-row">
                          <td className="file-name-cell">
                            <span>{file.name}</span>
                          </td>
                          <td className="file-type-cell">
                            <span
                              className={`file-type-badge ${file.type?.toLowerCase()}`}
                            >
                              {file.type}
                            </span>
                          </td>
                          <td className="file-status-cell">
                            <span
                              className={`file-status ${getStatusClass(file.status)}`}
                            >
                              {file.status === "Uploaded" ||
                              file.status === "All ok"
                                ? "All ok"
                                : file.status}
                            </span>
                            {file.issueCount > 0 && (
                              <span
                                className="info-icon"
                                title={`${file.issueCount} Issues`}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <circle
                                    cx="8"
                                    cy="8"
                                    r="7"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    fill="none"
                                  />
                                  <text
                                    x="8"
                                    y="11"
                                    textAnchor="middle"
                                    fontSize="10"
                                    fill="currentColor"
                                  >
                                    i
                                  </text>
                                </svg>
                              </span>
                            )}
                          </td>
                          <td className="file-modified-cell">
                            <div className="modified-content">
                              <span className="modified-date">
                                {formatModifiedDate(file.modified).date}
                              </span>
                              <span className="modified-time">
                                {formatModifiedDate(file.modified).time}
                              </span>
                            </div>
                          </td>
                          <td className="file-actions-cell">
                            <div
                              className="file-action-menu-wrapper"
                              ref={
                                activeActionMenu === file.id
                                  ? actionMenuRef
                                  : null
                              }
                            >
                              <button
                                className="file-action-btn"
                                onClick={(e) => toggleActionMenu(file.id, e)}
                                disabled={
                                  file.status.toLowerCase() === "mapped" ||
                                  file.status.toLowerCase() ===
                                    "data triage process initiated" ||
                                  getHeaderStatus(file.status)
                                }
                              >
                                <MoreVertical
                                  width={16}
                                  height={16}
                                  color="#A4A3B1"
                                />
                              </button>
                              {activeActionMenu === file.id && (
                                <div className="file-action-dropdown">
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleReplace(file)}
                                  >
                                    <RefreshIcon
                                      width={16}
                                      height={16}
                                      color="#A4A3B1"
                                    />
                                    <span>Replace</span>
                                  </button>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleDelete(file)}
                                  >
                                    <DeleteIcon
                                      width={16}
                                      height={16}
                                      color="#A4A3B1"
                                    />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <UploadNewFile
        show={showUploadModal}
        onClose={handleCloseUploadModal}
        onDataReceivedFlowChange={onDataReceivedFlowChange}
      />

      <ReviewMappingsModal
        show={showReviewMappings}
        onClose={handleReviewMappingsClose}
        onConfirm={handleReviewMappingsConfirm}
        onIncompleteData={onIncompleteData}
        uploadedFiles={fileSections}
      />

      <ReplaceFileModal
        show={showReplaceModal}
        onClose={handleReplaceModalClose}
        fileToReplace={fileToReplace}
        onConfirmReplace={handleConfirmReplace}
        validateFile={validateReplacementFile}
        isUploading={isReplacing}
        uploadProgress={replaceUploadProgress}
      />

      {/* Disclaimer Modal */}
      <MotifModal
        show={showDisclaimerModal}
        onClose={handleCloseDisclaimerModal}
        size="md"
        className="upload-new-file-modal file-upload-disclaimer-modal"
      >
        <MotifModalHeader>
          <div className="disclaimer-header-content">
            <WarningTriangle width={24} height={24} color="#FFE600" />
            <span>{FILE_UPLOAD.IMPORTANT_DISCLAIMER}</span>
          </div>
        </MotifModalHeader>
        <MotifModalBody>
          <div className="disclaimer-body-content">
            <p>{FILE_UPLOAD.DISCLAIMER_TEXT_1}</p>
            <p>{FILE_UPLOAD.DISCLAIMER_TEXT_2}</p>
            <p>{FILE_UPLOAD.DISCLAIMER_TEXT_3}</p>
            <ul>
              <li>{FILE_UPLOAD.DISCLAIMER_LIST[0]}</li>
              <li>{FILE_UPLOAD.DISCLAIMER_LIST[1]}</li>
              <li>{FILE_UPLOAD.DISCLAIMER_LIST[2]}</li>
              <li>
                {FILE_UPLOAD.DISCLAIMER}
                <a
                  href={FILE_UPLOAD.DISCLAIMER_LINK_URL1}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {FILE_UPLOAD.DISCLAIMER_LINK_TEXT1}
                </a>
                {FILE_UPLOAD.DISCLAIMER_SUFFIX}
                <a
                  href={FILE_UPLOAD.DISCLAIMER_LINK_URL2}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {FILE_UPLOAD.DISCLAIMER_LINK_TEXT2}
                </a>
              </li>
            </ul>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <button
            className="disclaimer-cancel-btn"
            onClick={handleCloseDisclaimerModal}
          >
            {FILE_UPLOAD.CANCEL}
          </button>
          <button
            className="disclaimer-confirm-btn"
            onClick={handleDisclaimerConfirm}
          >
            {FILE_UPLOAD.CONFIRM_AND_CONTINUE}
          </button>
        </MotifModalFooter>
      </MotifModal>
    </div>
  );
};
